<template>
  <vddl-draggable
    :key="item.id"
    :draggable="item"
    :index="index"
    :wrapper="list"
    :dragstart="onDragStart"
    :moved="onMove"
    :canceled="onCancel"
    :type="type"
    :disable-if="!canEdit"
  >
    <div
      class="asset-ref"
      :draggable="true"
      @drag="onAssetDrag"
      @mousedown="onMousedownAsset"
      @mouseup="onMouseupAsset"
      @click="onClickAsset($event, item)"
      @contextmenu.prevent="onContextMenu"
    >
      <div class="asset-thumb">
        <img v-if="imageUrl" :src="imageUrl" />
        <svg-icon v-else icon="app-icons/audio-start" width="22" height="22" />
      </div>
      <div class="asset-name">
        {{ item.name + (extension ? '.' + extension : '') }}
      </div>
    </div>
  </vddl-draggable>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import emitter from 'tiny-emitter/instance';
import { SYS_PERMISSIONS, EVENT, DRAG_LIST } from '@/constants.js';
import SvgIcon from '../../all/SvgIcon.vue';

export default {
  components: {
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    list: {
      type: Array,
      default: () => [],
    },
    item: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    moveAction: {
      type: Function,
      default: () => {},
    },
    deleteAction: {
      type: Function,
      default: () => {},
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  computed: {
    ...mapGetters('project/data/assets', ['getExtension', 'getSmallImageUrl']),
    assetRef() {
      return this.list[this.index];
    },

    /**
     * Return the asset file extension
     */
    extension() {
      return this.getExtension(this.item.id);
    },

    /**
     * Return the url of the small image of the cover asset
     * @returns {String|null}
     */
    imageUrl() {
      return this.getSmallImageUrl(this.item.id);
    },
  },

  methods: {
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
    ]),
    ...mapActions('application/modals', [
      'setAudioAssetModal',
      'setAssetModal',
    ]),
    ...mapActions('application/drag', ['setItems', 'setSource', 'clear']),

    onDragStart() {
      this.setItems({
        value: {
          type: this.type,
          ids: [this.item.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.ASSETS,
          ids: [this.id],
          indexes: [this.index],
        },
      });
    },

    onMove(event) {
      this.moveAction();
    },

    onCancel() {
      this.clear();
    },

    onAssetDrag($event) {
      emitter.emit(EVENT.DRAGGING_ASSET, $event);
    },

    onMousedownAsset(event) {
      this.$emit('disableDrag', event);
    },

    onMouseupAsset(event) {
      this.$emit('enableDrag', event);
    },

    onClickAsset(event, item) {
      if (this.item.type === 'image' || this.item.type === 'template-image') {
        this.setAssetModal({ value: { id: this.item.id } });
      } else if (
        this.item.type === 'audio' ||
        this.item.type === 'template-audio'
      ) {
        this.setAudioAssetModal({ value: { id: this.item.id } });
      }
    },

    onContextMenu(event) {
      if (this.canEdit) {
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    async createContextMenu(x, y) {
      this.createItem({
        name: 'Remove reference',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteAction(this.id, this.index);
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.asset-ref {
  display: flex;
  background: #1d1f1f;
  border-radius: 4px;
  align-items: center;
  overflow: hidden;
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    border-radius: 4px;
    border: 2px solid transparent;
    transition: border 0.25s;
  }
  &:hover::after {
    border: 2px solid #db841e;
    transition: border 0.25s;
  }

  .asset-thumb {
    width: 36px;
    height: 36px;
    background: #0d0e0e;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
    }
  }

  .asset-name {
    padding: 0 12px 0;
    color: #bcbcbc;
    font-weight: 600;
    flex-shrink: 0;
    transition: color 2.5s;
  }
}
</style>
